import React from 'react';
import 'quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';
import styled from 'styled-components';
import { colors } from '../colors/color';

type HtmlEditorProps = {
    id: string,
    value: string,
    onChange: (content: string) => void,
    onBlur?: (e:React.FocusEvent<HTMLDivElement, Element>) => void,
    onFocus?: () => void,
    inErrorState?: boolean,
    placeholder?: string,
    readonly?: boolean,
    debugPanel?: boolean,
    backgroundColor?: string,
    toolbarColor?: string,
    innerRef?: React.LegacyRef<ReactQuill>,
};
export function HtmlEditor (props: HtmlEditorProps): JSX.Element
{
    function handleChange (content: string)
    {
        let modifiedContent = content;
        if(content.replace(/<(.|\n)*?>/g, '').trim().length === 0) {
            //textarea is still empty after removing html tags
            modifiedContent = '';
        }

        props.onChange(modifiedContent);
    }

    return (
        <div>
            <CustomEditor
                id={props.id}
                value={props.value}
                onChange={handleChange}
                onBlur={props.onBlur}
                onFocus={props.onFocus}
                inErrorState={props.inErrorState}
                placeholder={props.placeholder}
                readonly={props.readonly}
                innerRef={props.innerRef}
                backgroundColor={props.backgroundColor}
                toolbarColor={props.toolbarColor}
            />
            {props.debugPanel &&
                <div style={{ border: '1px solid black', padding: '5px', marginTop: '10px' }}>
                    <b style={{ fontSize: '16px' }}>content debugging:</b><br />
                    <br />
                    <div>{props.value}</div>
                </div>
            }
        </div>
    );
}

interface CustomEditorProps
{
    id: string,
    value?: string,
    onChange?: (content: string) => void,
    onBlur?: (e:React.FocusEvent<HTMLDivElement, Element>) => void,
    onFocus?: () => void,
    inErrorState?: boolean,
    placeholder?: string,
    readonly?: boolean,
    backgroundColor?:string,
    toolbarColor?: string,
    innerRef?: React.LegacyRef<ReactQuill>,
}

function CustomEditor (props: CustomEditorProps) 
{
    const modules = {
        toolbar: [
            //[{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'/*, 'blockquote'*/],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }/*, { 'indent': '-1' }, { 'indent': '+1' }*/],
            ['link'/*, 'image'*/],
            //['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'link', 'image'
    ];

    const wrapperRef = React.useRef<HTMLDivElement>(null);

    function handleBlur(e: React.FocusEvent<HTMLDivElement, Element>)
    {
        // If clicked inside the wrapper, such as the toolbar or editor, do not trigger onBlur
        if(wrapperRef.current?.contains(e.relatedTarget as Node))
        {
            return;
        }

        props.onBlur?.(e);
    }

    return (
        // onBlur does not work properly in ReactQuill, so we handle it in the StyledReactQuill component
        // https://stackoverflow.com/questions/47018722/react-quill-onblur-not-working
        <StyledReactQuill 
            disabled={props.readonly === true} 
            inErrorState={props.inErrorState} 
            onBlur={handleBlur}
            ref={wrapperRef}
            backgroundColor={props.backgroundColor}
            toolbarColor={props.toolbarColor}>

            <ReactQuill
                id={props.id}
                theme="snow"
                modules={modules}
                formats={formats}
                value={props.value}
                onChange={props.onChange}
                //onBlur={props.onBlur}
                onFocus={props.onFocus}
                placeholder={props.placeholder}
                readOnly={props.readonly}
                ref={props.innerRef}
            />
        </StyledReactQuill>
    );
}

const StyledReactQuill = styled.div<{ disabled: boolean, inErrorState?: boolean, toolbarColor?: string, backgroundColor?:string }>`
    
    button:hover {
        cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    }

    & .ql-toolbar {
        background-color: ${props => props.disabled 
            ? colors.inputState.disabled.background 
            : props.toolbarColor 
                ? props.toolbarColor 
                : colors.background.white} !important;
        svg .ql-stroke {
            stroke: ${props => props.disabled ? colors.inputState.disabled.text : colors.inputState.default.text } !important;
        }
        svg .ql-fill {
            fill: ${props => props.disabled ? colors.inputState.disabled.text : colors.inputState.default.text } !important;
        }
    }

    & .ql-disabled {
        background-color: ${colors.background.white} !important;
    }

    .ql-editor {
        color: ${props => props.inErrorState ? colors.inputState.error.text : colors.inputState.default.text };
        ${props => props.backgroundColor ? `background-color: ${props.backgroundColor};` : ''}
    }



    .ql-editor strong{
        font-weight:bold;
    }
`;